(function () {
  'use strict';

  // --- Constants & Config ---
  const EXPERIMENT_NAME = 'full-day-set-pdp-variant';
  const LOCAL_STORAGE_KEY = 'full-day-set-ab-variant';
  const VALID_VARIANTS = ['control', 'treatment'];

  // --- Helpers ---
  function getQueryParam(name) {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  }

  function trackEvent(name, properties = {}) {
    if (window.posthog) {
      window.posthog.capture(name, properties);
    } else {
      console.log(`[PostHog Mock Capture] Event: ${name}`, properties);
    }
  }

  // localStorage can throw in private mode / when cookies are blocked — never
  // let that leave the page hidden. Wrap access so it degrades to no-op.
  function safeGet(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }
  function safeSet(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* ignore */ }
  }

  function normalizeVariant(raw) {
    if (raw === true) return 'treatment';
    if (raw === false) return 'control';
    // Retired arms collapse into the 2-arm test: v2 layout IS control now; seed is gone.
    if (raw === 'treatment-v2') return 'control';
    if (raw === 'seed') return 'control';
    if (VALID_VARIANTS.indexOf(raw) !== -1) return raw;
    if (raw === 'test') return 'treatment';
    return null;
  }

  function getSharedExperimentState() {
    return window.__mkFullDaySetExperiment || null;
  }

  function waitForVariantResolution() {
    const shared = getSharedExperimentState();
    if (shared && shared.ready && typeof shared.ready.then === 'function') {
      return shared.ready.then((variant) => ({
        variant: normalizeVariant(variant) || 'control',
        source: shared.source || 'shared'
      }));
    }

    const urlVariant = normalizeVariant(getQueryParam('variant'));
    if (urlVariant) {
      return Promise.resolve({ variant: urlVariant, source: 'query' });
    }

    const storedVariant = normalizeVariant(safeGet(LOCAL_STORAGE_KEY));
    if (storedVariant) {
      return Promise.resolve({ variant: storedVariant, source: 'storage' });
    }

    return new Promise((resolve) => {
      if (!window.posthog || typeof window.posthog.onFeatureFlags !== 'function') {
        resolve({
          variant: Math.random() < 0.5 ? 'treatment' : 'control',
          source: 'random'
        });
        return;
      }

      let settled = false;
      const finish = (variant, source) => {
        if (settled) return;
        settled = true;
        resolve({
          variant: normalizeVariant(variant) || 'control',
          source: source || 'fallback'
        });
      };

      const timer = setTimeout(() => {
        finish(Math.random() < 0.5 ? 'treatment' : 'control', 'random');
      }, 1200);

      window.posthog.onFeatureFlags(() => {
        if (settled) return;
        clearTimeout(timer);
        const sharedKey = shared && typeof shared.key === 'string' ? shared.key : EXPERIMENT_NAME;
        const phFlag = normalizeVariant(window.posthog.getFeatureFlag(sharedKey));
        if (phFlag) {
          finish(phFlag, 'posthog');
          return;
        }
        finish(Math.random() < 0.5 ? 'treatment' : 'control', 'random');
      });
    });
  }

  // --- A/B Testing Switcher ---
  function initExperiment() {
    const container = document.getElementById('full-day-pdp-container');
    if (!container) return;

    waitForVariantResolution()
      .then(({ variant, source }) => {
        safeSet(LOCAL_STORAGE_KEY, variant);
        applyVariant(variant, source);
      })
      .catch((err) => {
        // Last-resort safety net: never leave the PDP hidden (opacity:0).
        console.error('[A/B Test] initExperiment failed — falling back to control:', err);
        try {
          applyVariant('control', 'error');
        } catch (e2) {
          container.classList.remove('full-day-pdp--loading');
          document.documentElement.classList.remove('mk-full-day-set-loading');
        }
      });
  }

  function applyVariant(variant, source) {
    const container = document.getElementById('full-day-pdp-container');
    // Dawn layout container — retired as a visible arm; kept ONLY as the canonical
    // <product-form> source that the active layout transplants. It is never shown.
    const sourceBox = document.getElementById('pdp-control-container');
    const treatmentBox = document.getElementById('pdp-treatment-container');
    // The v2 markup now powers the CONTROL arm of the 2-arm test.
    const controlLayoutBox = document.getElementById('pdp-treatment-v2-container');
    const bybSection = document.querySelector('.section-build-your-bundle');

    if (!container || !sourceBox) return;

    // Two-arm test: `treatment` = dense layout, `control` = lean (v2) layout.
    // Anything that isn't treatment falls back to control.
    const isTreatment = variant === 'treatment' && treatmentBox;
    const isControl = !isTreatment;
    console.log('[A/B Test] Applying ' + (isControl ? 'CONTROL' : 'TREATMENT') + ' variant layout');

    // --- Step 1: set the correct layout visibility (cheap, cannot throw) ---
    sourceBox.style.display = 'none'; // Dawn source layout is never shown
    if (treatmentBox) treatmentBox.style.display = isTreatment ? 'block' : 'none';
    if (controlLayoutBox) controlLayoutBox.style.display = isControl ? 'block' : 'none';
    if (bybSection) bybSection.style.display = 'none';

    // The <product-info> wrapper carries the Dawn gradient. It never shows now, so
    // always strip its background/padding to avoid a thin collapsed-gradient line.
    const controlWrapper = sourceBox.closest('product-info');
    if (controlWrapper) {
      controlWrapper.style.background = 'none';
      controlWrapper.style.padding = '0';
    }

    // Both arms use a custom layout — hide the default theme sticky CTA.
    const defaultSticky = document.querySelector('.sticky-add-to-cart');
    if (defaultSticky) defaultSticky.style.display = 'none';

    // --- Step 2: progressive enhancements, guarded so a single failure
    // degrades gracefully. The `finally` guarantees the page is revealed even
    // if an enhancement throws — a JS error can never leave the PDP blank. ---
    try {
      // Transplant the single Liquid product-form into the active layout.
      const productForm = sourceBox.querySelector('product-form');
      if (isTreatment) {
        const formPlaceholder = document.getElementById('treatment-form-placeholder');
        if (productForm && formPlaceholder) formPlaceholder.appendChild(productForm);
        attachVariantAttribution(productForm, variant, source);

        initStickyCTA(variant, source);
        initAccordionTriggers();
        initPromoCodeCopy();
        initTreatmentQuantitySelector();
      } else {
        // control = lean (v2) layout
        const formPlaceholder = document.getElementById('treatment-v2-form-placeholder');
        if (productForm && formPlaceholder) formPlaceholder.appendChild(productForm);
        attachVariantAttribution(productForm, variant, source);

        // Both arms get the persistent mobile Add-to-Cart pill.
        initStickyCTA(variant, source);
        initTreatmentV2PromoCodeCopy();
        initTreatmentV2QuantitySelector();
        initTreatmentV2Accordions();
      }

      // Open the cart drawer the instant Add to Cart is pressed (before the
      // server round-trip), so adding feels instantaneous.
      initOptimisticCartOpen();

      // Initialize mobile slider for all variants
      initSlider();

      // Extract first frames from UGC videos for reviews avatars
      initAvatarFrames();
      setTimeout(initAvatarFrames, 1000);
    } catch (err) {
      console.error('[A/B Test] Enhancement error:', err);
    } finally {
      // Remove FOUC loading state (always runs, even on error above)
      container.classList.remove('full-day-pdp--loading');
      document.documentElement.classList.remove('mk-full-day-set-loading');
    }

    // Track presentation in PostHog
    trackEvent('full_day_set_pdp_viewed', {
      experiment_name: EXPERIMENT_NAME,
      experiment_variant: variant,
      experiment_source: source || 'unknown'
    });

    // Tag EVERY visitor with the resolved arm so analysis has ~100% coverage.
    // NOTE: true SSR of the PostHog flag isn't possible in a stock Liquid theme
    // (no server render step before the HTML ships). Instead we make client-side
    // tagging reliable: register a super-property that rides every subsequent
    // event, and emit an explicit exposure so the arm is recorded even when the
    // visitor was assigned by the local random fallback rather than the server
    // flag. Analyze the experiment on the `full_day_set_variant` property (full
    // coverage), NOT the late-loading $feature flag (~25% coverage).
    if (window.posthog && typeof window.posthog.register === 'function') {
      window.posthog.register({
        full_day_set_variant: variant,
        full_day_set_variant_source: source || 'unknown'
      });
    }
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture('$feature_flag_called', {
        $feature_flag: EXPERIMENT_NAME,
        $feature_flag_response: variant,
        experiment_source: source || 'unknown'
      });
    }

    // Initialize full purchase funnel tracking
    initConversionTracking(variant, source || 'unknown');
  }

  // Attribution: stamp the resolved A/B arm onto the product form as hidden
  // line-item properties so they ride the /cart/add request and land on the
  // Shopify ORDER (order.line_items[].properties._ab_variant). This is what
  // fixes purchase attribution — the order no longer loses which arm converted.
  // `_`-prefixed property names are stored on the order but hidden from the
  // cart/checkout UI.
  function attachVariantAttribution(productForm, variant, source) {
    if (!productForm) return;
    const formEl = productForm.matches && productForm.matches('form')
      ? productForm
      : productForm.querySelector('form');
    if (!formEl) return;
    const set = (name, value) => {
      let input = formEl.querySelector('input[name="' + name + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        formEl.appendChild(input);
      }
      input.value = value;
    };
    set('properties[_ab_variant]', variant);
    set('properties[_ab_source]', source || 'unknown');
  }

  // --- Purchase Funnel Tracking ---
  function initConversionTracking(variant, source) {
    try {
      // Track when ANY Add to Cart form is submitted on the page
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (form.action && (form.action.includes('/cart/add') || form.action.includes('/cart/change'))) {
          trackEvent('add_to_cart_clicked', {
            experiment_variant: variant,
            experiment_source: source,
            product_title: 'The Full Day Incense Set'
          });
        }
      });

      // Track cart drawer open (if it opens)
      const cartDrawerObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const isCartOpen = document.querySelector('cart-drawer')?.classList.contains('active');
            if (isCartOpen) {
              trackEvent('cart_drawer_opened', {
                experiment_variant: variant,
                experiment_source: source
              });
            }
          }
        });
      });

      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        cartDrawerObserver.observe(drawer, { attributes: true });
      }
    } catch (err) {
      console.warn('[A/B Test] Conversion tracking init error:', err);
    }
  }

  // --- Mobile Slider / Gallery ---
  function initSlider() {
    document.querySelectorAll('treatment-gallery, .mk-mobile-slider').forEach(function (gallery) {
      // Skip hidden galleries (inactive variant container)
      if (gallery.offsetParent === null) return;

      const isTreatment = gallery.tagName === 'TREATMENT-GALLERY';
      const track = isTreatment
        ? gallery.querySelector('.treatment-gallery__track')
        : gallery.querySelector('.mk-stack-gallery');
      if (!track) return;
      const items = track.querySelectorAll(isTreatment ? '.treatment-gallery__item' : '.mk-stack-gallery__item');
      var dotsContainer = isTreatment
        ? gallery.querySelector('.treatment-gallery__dots')
        : gallery.querySelector('.mk-stack-gallery__dots');
      if (items.length === 0) return;

      // Create dots dynamically if container is empty
      if (dotsContainer && dotsContainer.children.length === 0) {
        items.forEach(function (_, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = isTreatment ? 'treatment-gallery__dot' : 'mk-stack-gallery__dot';
          if (i === 0) dot.classList.add('active');
          dot.setAttribute('data-index', String(i));
          dot.addEventListener('click', function () {
            stopAutoplay();
            goToSlide(parseInt(this.getAttribute('data-index')));
            startAutoplay();
          });
          dotsContainer.appendChild(dot);
        });
      }

      var dots = dotsContainer ? dotsContainer.querySelectorAll(isTreatment ? '.treatment-gallery__dot' : '.mk-stack-gallery__dot') : [];

      let currentIndex = 0;
      let autoplayTimer = null;
      const autoplaySpeed = parseInt(gallery.dataset.speed || '5', 10) * 1000;
      const isAutoplayEnabled = gallery.dataset.autoplay === 'true';

      // Touch/Swipe mechanics
      let startX = 0;
      let currentX = 0;
      let isDragging = false;

      function getStep() {
        if (items.length < 2) return gallery.offsetWidth;
        var rect0 = items[0].getBoundingClientRect();
        var rect1 = items[1].getBoundingClientRect();
        return rect1.left - rect0.left;
      }

      function getTranslateX() {
        if (window.innerWidth < 750) {
          return -currentIndex * getStep();
        }
        return 0;
      }

      function updateGallery() {
        if (window.innerWidth < 750) {
          track.style.transform = 'translateX(' + getTranslateX() + 'px)';
        } else {
          track.style.transform = '';
        }
        dots.forEach(function (dot, i) {
          dot.classList.toggle('active', i === currentIndex);
        });
      }

      function goToSlide(index) {
        if (index < 0) index = 0;
        if (index >= items.length) index = items.length - 1;
        currentIndex = index;
        updateGallery();
      }

      function nextSlide() {
        var nextIndex = currentIndex + 1;
        if (nextIndex >= items.length) nextIndex = 0;
        goToSlide(nextIndex);
      }

      function startAutoplay() {
        if (!isAutoplayEnabled) return;
        stopAutoplay();
        autoplayTimer = setInterval(nextSlide, autoplaySpeed);
      }

      function stopAutoplay() {
        if (autoplayTimer) {
          clearInterval(autoplayTimer);
          autoplayTimer = null;
        }
      }

      // Touch Event Handlers
      track.addEventListener('touchstart', function (e) {
        stopAutoplay();
        startX = e.touches[0].clientX;
        isDragging = true;
        track.style.transition = 'none';
      }, { passive: true });

      track.addEventListener('touchmove', function (e) {
        if (!isDragging) return;
        currentX = e.touches[0].clientX;
        var diffX = currentX - startX;
        var baseTranslate = getTranslateX();
        track.style.transform = 'translateX(' + (baseTranslate + diffX) + 'px)';
      }, { passive: true });

      track.addEventListener('touchend', function (e) {
        if (!isDragging) return;
        isDragging = false;
        track.style.transition = 'transform 0.3s cubic-bezier(0.25, 1, 0.5, 1)';
        var diffX = currentX - startX;

        if (diffX < -50 && currentIndex < items.length - 1) {
          currentIndex++;
        } else if (diffX > 50 && currentIndex > 0) {
          currentIndex--;
        }
        goToSlide(currentIndex);
        startAutoplay();
      });

      // Handle viewport resizing
      window.addEventListener('resize', updateGallery);

      // Start
      goToSlide(0);
      startAutoplay();
    });
  }


  // --- Promo Code Copying ---
  function initPromoCodeCopy() {
    const copyBtn = document.getElementById('treatmentPromoCopyBtn');
    if (!copyBtn) return;

    copyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const code = copyBtn.getAttribute('data-code') || 'Ritual 10';
      navigator.clipboard.writeText(code).then(() => {
        copyBtn.textContent = 'Copied!';
        copyBtn.classList.add('copied');
        
        // Reset button text after 3 seconds
        setTimeout(() => {
          copyBtn.textContent = 'Copy';
          copyBtn.classList.remove('copied');
        }, 3000);
      }).catch((err) => {
        console.error('Failed to copy text: ', err);
      });
    });
  }

  // --- Treatment Variant Quantity Selector ---
  function initTreatmentQuantitySelector() {
    const formPlaceholder = document.getElementById('treatment-form-placeholder');
    if (!formPlaceholder) return;
    const form = formPlaceholder.querySelector('form');
    if (!form) return;
    
    // Check if already initialized to prevent duplicate runs
    if (form.querySelector('.treatment-purchase-bar')) return;

    // Ensure there is a hidden quantity input inside the form
    let qtyInput = form.querySelector('input[name="quantity"]');
    if (!qtyInput) {
      qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = 'quantity';
      qtyInput.value = '1';
      form.appendChild(qtyInput);
    } else {
      qtyInput.type = 'hidden';
      qtyInput.value = '1';
    }

    // Get the ATC button container
    const buttonsContainer = form.querySelector('.product-form__buttons');
    const atcButton = form.querySelector('button[type="submit"]');
    if (!buttonsContainer || !atcButton) return;

    // Create the purchase bar flex container
    const purchaseBar = document.createElement('div');
    purchaseBar.className = 'treatment-purchase-bar';

    // Style the ATC button specifically for treatment layout
    atcButton.classList.add('treatment-atc-button');

    // Move ATC button inside the purchase bar
    buttonsContainer.insertBefore(purchaseBar, atcButton);
    purchaseBar.appendChild(atcButton);
  }

  // --- Optimistic cart-drawer open ---
  // Dawn waits for /cart/add to return the server-rendered cart-drawer section
  // (line items + reward bar + upsell) before the drawer slides in — so on a
  // slow connection the tap feels dead for a few hundred ms and users bail.
  // Open the drawer immediately with a spinner overlay on press; Dawn's normal
  // renderContents then fills it and the overlay clears. Fail-safe: the overlay
  // also self-clears on any drawer mutation and after a hard timeout, so it can
  // never get stuck even if the add errors.
  function initOptimisticCartOpen() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || cartDrawer.__mkOptimisticBound) return;
    cartDrawer.__mkOptimisticBound = true;

    let clearTimer = null;
    const clearOptimistic = () => {
      clearTimeout(clearTimer);
      cartDrawer.classList.remove('cart-drawer--optimistic');
    };
    const showOptimistic = () => {
      cartDrawer.classList.add('animate', 'active', 'cart-drawer--optimistic');
      document.body.classList.add('overflow-hidden');
      clearTimeout(clearTimer);
      clearTimer = setTimeout(clearOptimistic, 6000); // safety net — never stick
    };

    // Fire on the real ATC press in whichever arm's placeholder holds the form.
    ['#treatment-form-placeholder', '#treatment-v2-form-placeholder'].forEach((sel) => {
      const ph = document.querySelector(sel);
      if (!ph) return;
      ph.addEventListener('click', (e) => {
        const btn = e.target.closest('button[type="submit"]');
        if (btn && !btn.disabled && btn.getAttribute('aria-disabled') !== 'true') {
          showOptimistic();
        }
      });
    });

    // Clear the overlay as soon as Dawn morphs real content into the drawer.
    const mo = new MutationObserver(() => clearOptimistic());
    mo.observe(cartDrawer, { childList: true, subtree: true });

    // If the add is REJECTED (inventory cap, quantity rule — e.g. "you can only
    // add 1 of this item"), product-form.js renders the reason on the PDP — but
    // the optimistically-opened drawer covers it, so the tap just feels dead.
    // Close the drawer on cart errors so the message is actually visible.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartError) {
      subscribe(PUB_SUB_EVENTS.cartError, () => {
        clearOptimistic();
        if (cartDrawer.classList.contains('active') && typeof cartDrawer.close === 'function') {
          cartDrawer.close();
        }
      });
    }
  }

  // --- Sticky CTA Pill (both arms) ---
  function initStickyCTA(variant, source) {
    const stickyPill = document.getElementById('treatment-sticky-pill');
    const stickyPillBtn = document.getElementById('treatment-sticky-pill-btn');
    if (!stickyPill || !stickyPillBtn) return;

    // The product-form is transplanted into a different placeholder per arm, so
    // resolve the ATC button from the ACTIVE arm's placeholder (not hardcoded).
    const placeholderSel = variant === 'treatment'
      ? '#treatment-form-placeholder'
      : '#treatment-v2-form-placeholder';
    const getSubmit = () => document.querySelector(placeholderSel + ' button[type="submit"]');

    // Reveal the pill once the primary Add to Cart button leaves the viewport.
    // On desktop the v2 buy-box column is position:sticky so the ATC stays in
    // view and the pill stays hidden; on mobile the ATC scrolls away and the
    // pill provides the persistent CTA.
    const formSubmitBtn = getSubmit();
    if (formSubmitBtn && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          stickyPill.classList.toggle('treatment-sticky-pill--visible', !entry.isIntersecting);
        });
      }, {
        threshold: 0,
        rootMargin: '0px 0px -100px 0px' // offset from bottom
      });
      observer.observe(formSubmitBtn);
    } else {
      // Fallback scroll listener if the form button is delayed / no IO support.
      window.addEventListener('scroll', () => {
        stickyPill.classList.toggle('treatment-sticky-pill--visible', window.scrollY > 600);
      });
    }

    // Pill click proxies to the active arm's real Add to Cart button.
    stickyPillBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const actualSubmit = getSubmit();
      if (actualSubmit) {
        actualSubmit.click();
        const titleEl = document.querySelector('.treatment-buy-box__title, .treatment-v2-title');
        trackEvent('sticky_cta_clicked', {
          experiment_variant: variant,
          experiment_source: source,
          product_title: titleEl ? titleEl.textContent.trim() : 'The Full Day Incense Set'
        });
      }
    });
  }

  // --- Accordion Interactivity ---
  function initAccordionTriggers() {
    // Star rating scroll interaction
    const starsTrigger = document.getElementById('treatment-stars-trigger');
    if (starsTrigger) {
      starsTrigger.addEventListener('click', (e) => {
        // Wait, if it links to `#ml-difference`, let's smooth scroll it
        const target = document.querySelector('#ml-difference') || document.querySelector('.jdgm-review-widget');
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  }




  // Client-side override for the "Aromas Notes → Karnataka" card. Shopify's
  // Liquid page cache is slow to reflect section edits for this specific card;
  // running the swap in JS bypasses that entirely. Idempotent — safe to call
  // repeatedly.
  function initAromasKarnatakaOverride() {
    // Find the tab button whose text contains "aroma" (case-insensitive), then
    // locate its associated tab content by data-tab / id mapping.
    const buttons = Array.from(document.querySelectorAll('.tab-btn'));
    const aromaBtn = buttons.find(b => /aroma/i.test(b.textContent || ''));
    if (!aromaBtn) return;
    const targetId = aromaBtn.getAttribute('data-tab');
    if (!targetId) return;
    const panel = document.getElementById(targetId);
    if (!panel) return;

    const cards = panel.querySelectorAll('.card');
    cards.forEach((card) => {
      const titleEl = card.querySelector('.tab-card-title');
      if (!titleEl) return;
      const title = (titleEl.textContent || '').toLowerCase();
      if (!title.includes('karnataka')) return;
      if (card.dataset.mkOverride === 'true') return;

      titleEl.textContent = 'The Karnataka Notes';
      const img = card.querySelector('.card-image');
      if (img) {
        img.src = 'https://cdn.shopify.com/s/files/1/0676/6457/6600/files/Karnatka_notes.jpg?v=1783117017';
        img.removeAttribute('srcset');
        img.alt = 'Karnataka aroma notes';
      }
      const desc = card.querySelector('.card-desc') || card.querySelector('div:last-of-type');
      if (desc) {
        desc.innerHTML = '<div class="metafield-rich_text_field"><p>Warm sandalwood opens the heart of this blend, grounded by the quiet fire of black pepper. Cinnamon brings a sweet, spiced finish that fills your space with calm focus.</p></div>';
      }
      card.dataset.mkOverride = 'true';
    });

    // Also handle mobile accordion — its titles contain "aroma" as accordion-header text.
    const accHeaders = Array.from(document.querySelectorAll('.mobile-accordion .accordion-header'));
    accHeaders.forEach((header) => {
      if (!/aroma/i.test(header.textContent || '')) return;
      const content = header.nextElementSibling;
      if (!content) return;
      const accCards = content.querySelectorAll('.card');
      accCards.forEach((card) => {
        const titleEl = card.querySelector('.tab-card-title');
        if (!titleEl) return;
        if (!(titleEl.textContent || '').toLowerCase().includes('karnataka')) return;
        if (card.dataset.mkOverride === 'true') return;

        titleEl.textContent = 'The Karnataka Notes';
        const img = card.querySelector('.card-image');
        if (img) {
          img.src = 'https://cdn.shopify.com/s/files/1/0676/6457/6600/files/Karnatka_notes.jpg?v=1783117017';
          img.removeAttribute('srcset');
          img.alt = 'Karnataka aroma notes';
        }
        // Mobile accordion uses a plain <div> not .card-desc — find the div sibling
        // of the img/title that contains the description
        const descDiv = Array.from(card.children).find(c => c.tagName === 'DIV' && !c.classList.contains('card-image'));
        if (descDiv) {
          descDiv.innerHTML = '<div class="metafield-rich_text_field"><p>Warm sandalwood opens the heart of this blend, grounded by the quiet fire of black pepper. Cinnamon brings a sweet, spiced finish that fills your space with calm focus.</p></div>';
        }
        card.dataset.mkOverride = 'true';
      });
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { initExperiment(); initAromasKarnatakaOverride(); });
  } else {
    initExperiment();
    initAromasKarnatakaOverride();
  }
  // Also retry after 500ms in case tabs render lazily
  setTimeout(initAromasKarnatakaOverride, 500);

  // Also hook into Shopify Theme Editor select event for live updates
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.classList.contains('section-build-your-bundle') || e.target.id.includes('main')) {
      initExperiment();
    }
  });

  // Use UGC video first frames as avatar sources. Prefers the `poster` attribute
  // when present (fast, one Shopify CDN request); otherwise falls back to
  // extracting a frame from the video source itself via a canvas. This keeps
  // avatars working even when the UGC blocks don't have a poster configured.
  function initAvatarFrames() {
    // Only fill avatars inside the VISIBLE variant container. Both control and
    // treatment containers coexist in the DOM (one is display:none) and each
    // has its own set of 5 avatar imgs — targeting both consumes frame slots
    // for hidden elements and leaves visible slots as duplicates or initials.
    const visibleContainer =
      ['#pdp-treatment-v2-container', '#pdp-treatment-container']
        .map((sel) => document.querySelector(sel))
        .find((el) => el && getComputedStyle(el).display !== 'none');
    const scope = visibleContainer || document;
    const avatars = scope.querySelectorAll('.treatment-social-proof__avatars img');

    // Dedupe UGC videos by source URL — if the same video file is used in
    // multiple UGC blocks its first frame would be identical, causing repeats.
    const allUgc = Array.from(document.querySelectorAll('.moksae_ugc_video, .ml-reviews-video-element'));
    const seenUrls = new Set();
    const ugcVideos = allUgc.filter((v) => {
      const src = (v.querySelector('source') && v.querySelector('source').getAttribute('src')) || v.getAttribute('poster') || v.getAttribute('src');
      if (!src || seenUrls.has(src)) return false;
      seenUrls.add(src);
      return true;
    });

    if (avatars.length === 0 || ugcVideos.length === 0) return;
    if (document.body.dataset.mkAvatarFramesDone === 'true') return;
    document.body.dataset.mkAvatarFramesDone = 'true';

    // Only fill each avatar once — don't clobber if it's already been swapped.
    const slots = Array.from(avatars).filter((img) => !img.dataset.mkFrameApplied);
    let cursor = 0;

    function assignSrc(dataUrl) {
      while (cursor < slots.length && slots[cursor].dataset.mkFrameApplied === 'true') cursor++;
      if (cursor >= slots.length) return false;
      const img = slots[cursor];
      img.src = dataUrl;
      img.dataset.mkFrameApplied = 'true';
      cursor++;
      return true;
    }

    ugcVideos.forEach((video) => {
      if (cursor >= slots.length) return;

      // Fast path — poster is already set (either via block.settings.poster or
      // block.settings.video.preview_image on the server).
      const posterUrl = video.getAttribute('poster');
      if (posterUrl && posterUrl.length > 0) {
        assignSrc(posterUrl);
        return;
      }

      // Fallback — no poster set. Grab the video source and extract the first
      // frame via a hidden <video>+<canvas> pair. Runs off-screen so nothing
      // visible changes until we set the img.src at the end.
      const source = video.querySelector('source');
      const srcUrl = source && source.getAttribute('src');
      if (!srcUrl) return;

      const probe = document.createElement('video');
      probe.crossOrigin = 'anonymous';
      probe.muted = true;
      probe.playsInline = true;
      probe.preload = 'auto';
      probe.src = srcUrl;
      probe.style.position = 'absolute';
      probe.style.left = '-9999px';
      probe.style.width = '1px';
      probe.style.height = '1px';
      probe.style.opacity = '0';

      let handled = false;
      const cleanup = () => {
        probe.removeEventListener('loadeddata', onData);
        probe.removeEventListener('seeked', onSeeked);
        probe.removeEventListener('error', onError);
        if (probe.parentNode) probe.parentNode.removeChild(probe);
      };

      const draw = () => {
        if (handled) return;
        try {
          const w = probe.videoWidth || 160;
          const h = probe.videoHeight || 160;
          const size = 160;
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          // Center-crop to a square before scaling to keep faces framed.
          const crop = Math.min(w, h);
          const sx = (w - crop) / 2;
          const sy = (h - crop) / 2;
          ctx.drawImage(probe, sx, sy, crop, crop, 0, 0, size, size);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
          if (dataUrl && dataUrl.length > 100) assignSrc(dataUrl);
        } catch (err) {
          // toDataURL throws on cross-origin taint; leave the initial avatar in place.
        }
        handled = true;
        cleanup();
      };

      const onSeeked = () => draw();
      const onData = () => {
        try {
          probe.currentTime = Math.min(0.1, (probe.duration || 1) * 0.05);
        } catch (e) {
          draw();
        }
      };
      const onError = () => { handled = true; cleanup(); };

      probe.addEventListener('loadeddata', onData);
      probe.addEventListener('seeked', onSeeked);
      probe.addEventListener('error', onError);

      document.body.appendChild(probe);
    });
  }

  // ============================================================
  //  Floating UGC Video Bubble — auto-rotating customer videos
  // ============================================================
  function initUgcFloatBubble() {
    // Only show on desktop
    if (window.innerWidth <= 989) return;

    let bubble = document.getElementById('ugc-float-bubble');

    // If bubble HTML doesn't exist in the DOM (control variant), create it dynamically
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'ugc-float-bubble';
      bubble.id = 'ugc-float-bubble';
      bubble.style.display = 'none';
      bubble.innerHTML = `
        <button class="ugc-float-bubble__close" type="button" aria-label="Close video">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <div class="ugc-float-bubble__video-wrap">
          <video class="ugc-float-bubble__video" playsinline loop muted preload="metadata"></video>
          <div class="ugc-float-bubble__progress"><div class="ugc-float-bubble__progress-bar"></div></div>
        </div>
        <div class="ugc-float-bubble__controls">
          <button class="ugc-float-bubble__btn" type="button" aria-label="Unmute" data-ugc-float-sound>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"></path><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>
          </button>
          <span class="ugc-float-bubble__label">Customer Review</span>
        </div>
      `;
    }

    // Anchor to <body> so `position: fixed` sticks to the viewport bottom.
    // (An ancestor with backdrop-filter/transform — e.g. .treatment-buy-box —
    // would otherwise become the containing block and break fixed positioning.)
    if (bubble.parentElement !== document.body) {
      document.body.appendChild(bubble);
    }

    const video = bubble.querySelector('.ugc-float-bubble__video');
    const progressBar = bubble.querySelector('.ugc-float-bubble__progress-bar');
    const closeBtn = bubble.querySelector('.ugc-float-bubble__close');
    const soundBtn = bubble.querySelector('[data-ugc-float-sound]');
    if (!video) return;

    const MUTE_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"></path><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>`;
    const SOUND_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"></path><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>`;

    // Collect video sources from UGC grid on the page
    const ugcVideoEls = document.querySelectorAll('.moksae_ugc_video');
    const sources = [];
    ugcVideoEls.forEach(v => {
      const src = v.querySelector('source');
      if (src && src.getAttribute('src')) {
        sources.push({
          url: src.getAttribute('src'),
          poster: v.getAttribute('poster') || ''
        });
      }
    });

    if (sources.length === 0) return;

    let currentIndex = 0;
    let rotationInterval = null;
    let progressInterval = null;
    const ROTATION_TIME = 10000; // 10 seconds per video
    const PROGRESS_STEP = 300; // update progress every 300ms

    function loadVideo(index) {
      const src = sources[index];
      video.src = src.url;
      if (src.poster) video.poster = src.poster;
      video.load();
      video.play().catch(() => {});

      // Reset and animate progress bar
      progressBar.style.transition = 'none';
      progressBar.style.width = '0%';
      void progressBar.offsetWidth; // force reflow

      let elapsed = 0;
      clearInterval(progressInterval);
      progressInterval = setInterval(() => {
        elapsed += PROGRESS_STEP;
        const pct = Math.min((elapsed / ROTATION_TIME) * 100, 100);
        progressBar.style.transition = 'width 0.3s linear';
        progressBar.style.width = pct + '%';
      }, PROGRESS_STEP);
    }

    function rotateToNext() {
      currentIndex = (currentIndex + 1) % sources.length;
      loadVideo(currentIndex);
    }

    function startRotation() {
      clearInterval(rotationInterval);
      rotationInterval = setInterval(rotateToNext, ROTATION_TIME);
    }

    function stopRotation() {
      clearInterval(rotationInterval);
      clearInterval(progressInterval);
    }

    // Close button
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      stopRotation();
      video.pause();
      video.src = '';
      bubble.style.display = 'none';
    });

    // Sound toggle
    soundBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      video.muted = !video.muted;
      soundBtn.innerHTML = video.muted ? MUTE_SVG : SOUND_SVG;
      soundBtn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
    });

    // Click on video to pause/play
    bubble.querySelector('.ugc-float-bubble__video-wrap').addEventListener('click', () => {
      if (video.paused) {
        video.play().catch(() => {});
        startRotation();
      } else {
        video.pause();
        stopRotation();
      }
    });

    // Keep the bubble clear of the bottom sticky bar (Add to cart) when it
    // overlaps — lift it above the bar instead of sitting under it.
    const BUBBLE_GAP = 16;
    function repositionAboveStickyBar() {
      const bars = document.querySelectorAll('.sticky-add-to-cart, .treatment-sticky-pill');
      const br = bubble.getBoundingClientRect();
      let occupied = 0; // px the overlapping bar occupies from the viewport bottom
      bars.forEach((bar) => {
        const s = getComputedStyle(bar);
        if (s.display === 'none' || s.visibility === 'hidden') return;
        const r = bar.getBoundingClientRect();
        if (r.height === 0) return;
        const anchoredToBottom = r.bottom > window.innerHeight - 5 && r.top < window.innerHeight;
        const overlapsHorizontally = r.right > br.left && r.left < br.right;
        if (anchoredToBottom && overlapsHorizontally) {
          occupied = Math.max(occupied, window.innerHeight - r.top);
        }
      });
      bubble.style.bottom = (occupied > 0 ? occupied + BUBBLE_GAP : 20) + 'px';
    }

    let repositionScheduled = false;
    function scheduleReposition() {
      if (repositionScheduled) return;
      repositionScheduled = true;
      requestAnimationFrame(() => {
        repositionScheduled = false;
        repositionAboveStickyBar();
      });
    }
    window.addEventListener('scroll', scheduleReposition, { passive: true });
    window.addEventListener('resize', scheduleReposition, { passive: true });

    // Start after a short delay so the page settles
    setTimeout(() => {
      bubble.style.display = '';
      loadVideo(0);
      startRotation();
      repositionAboveStickyBar();
    }, 2000);
  }

  // Kick off the floating bubble on treatment variant only
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(initUgcFloatBubble, 500);
    });
  } else {
    setTimeout(initUgcFloatBubble, 500);
  }

  /* ----------------------------------------------------------------
     Gallery tile height balancer
     Sets a CSS custom property --row-h on the gallery track so that
     grid-template-rows sizes each row to exactly half the 9:16 video
     height (minus the gap). Uses the video's WIDTH to compute height,
     avoiding the circular dependency where measuring height depends on
     the very grid rows we're trying to size.
     ---------------------------------------------------------------- */
  function balanceGalleryTileHeights() {
    var activeContainer;
    ['#pdp-treatment-v2-container', '#pdp-treatment-container'].some(function (sel) {
      var el = document.querySelector(sel);
      if (el && getComputedStyle(el).display !== 'none') {
        activeContainer = el;
        return true;
      }
    });
    if (!activeContainer) return;

    var gallery = activeContainer.querySelector('.mk-stack-gallery, .treatment-gallery__track');
    if (!gallery) return;

    // Only run in grid mode — mobile uses flex slider with aspect-ratio
    if (getComputedStyle(gallery).display !== 'grid') return;

    var gap = parseInt(getComputedStyle(gallery).rowGap || getComputedStyle(gallery).gap) || 12;
    var video = gallery.querySelector('[data-index="mk-video"]');
    if (!video) return;

    /* Use the actual rendered video height when available (most reliable).
       Fall back to computing from width × 16/9 for initial render before
       the grid has laid out the video at its final size. */
    var videoRect = video.getBoundingClientRect();
    var videoH = videoRect.height;
    if (videoH <= 0) {
      var videoW = videoRect.width;
      if (videoW <= 0) return;
      videoH = videoW * 16 / 9;
    }
    var rowH = Math.round((videoH - gap) / 2);

    // Set CSS custom property for grid-template-rows
    gallery.style.setProperty('--row-h', rowH + 'px');

    // Also set explicit height on each tile as a fallback in case
    // grid-template-rows hasn't taken effect yet
    var t1 = video.nextElementSibling;
    var t2 = t1 && t1.nextElementSibling;
    if (t1) t1.style.setProperty('height', rowH + 'px', 'important');
    if (t2 && !t2.matches('[data-index="mk-video"], [data-media-type="video"]')) {
      t2.style.setProperty('height', rowH + 'px', 'important');
    }
  }

  function watchGalleryTiles() {
    balanceGalleryTileHeights();
    // ResizeObserver for responsive resizing — observe both the gallery and the video
    if (window.ResizeObserver) {
      document.querySelectorAll('.mk-stack-gallery, .treatment-gallery__track, [data-index="mk-video"]').forEach(function (el) {
        new ResizeObserver(balanceGalleryTileHeights).observe(el);
      });
    } else {
      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(balanceGalleryTileHeights, 150);
      }, { passive: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchGalleryTiles);
  } else {
    watchGalleryTiles();
  }
  // Retries to outlast A/B experiment resolution and layout settling
  setTimeout(balanceGalleryTileHeights, 300);
  setTimeout(balanceGalleryTileHeights, 800);
  setTimeout(balanceGalleryTileHeights, 1500);
  setTimeout(balanceGalleryTileHeights, 3000);

  // --- Treatment V2 Copy Promo Code ---
  function initTreatmentV2PromoCodeCopy() {
    const copyBtn = document.getElementById('treatment-v2PromoCopyBtn');
    if (!copyBtn) return;

    copyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const code = copyBtn.getAttribute('data-code') || 'Ritual 10';
      navigator.clipboard.writeText(code).then(() => {
        copyBtn.textContent = 'Copied!';
        copyBtn.classList.add('copied');
        
        // Reset button text after 3 seconds
        setTimeout(() => {
          copyBtn.textContent = 'Copy';
          copyBtn.classList.remove('copied');
        }, 3000);
      }).catch((err) => {
        console.error('Failed to copy text: ', err);
      });
    });
  }

  // --- Treatment V2 Quantity Selector ---
  function initTreatmentV2QuantitySelector() {
    const formPlaceholder = document.getElementById('treatment-v2-form-placeholder');
    if (!formPlaceholder) return;
    const form = formPlaceholder.querySelector('form');
    if (!form) return;
    
    // Check if already initialized to prevent duplicate runs
    if (form.querySelector('.treatment-v2-purchase-bar')) return;

    // Ensure there is a hidden quantity input inside the form
    let qtyInput = form.querySelector('input[name="quantity"]');
    if (!qtyInput) {
      qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = 'quantity';
      qtyInput.value = '1';
      form.appendChild(qtyInput);
    } else {
      qtyInput.type = 'hidden';
      qtyInput.value = '1';
    }

    // Get the ATC button container
    const buttonsContainer = form.querySelector('.product-form__buttons');
    const atcButton = form.querySelector('button[type="submit"]');
    if (!buttonsContainer || !atcButton) return;

    // Create the purchase bar flex container
    const purchaseBar = document.createElement('div');
    purchaseBar.className = 'treatment-v2-purchase-bar';

    // Style the ATC button specifically for treatment-v2 layout
    atcButton.classList.add('treatment-v2-atc-button');

    // Move ATC button inside the purchase bar
    buttonsContainer.insertBefore(purchaseBar, atcButton);
    purchaseBar.appendChild(atcButton);
  }

  // --- Treatment V2 Accordions (Optional custom event mapping / state synchronization) ---
  function initTreatmentV2Accordions() {
    const accordions = document.querySelectorAll('#pdp-treatment-v2-container details.treatment-v2-accordion');
    accordions.forEach(details => {
      details.addEventListener('toggle', () => {
        const icon = details.querySelector('.toggle-icon');
        if (icon) {
          icon.textContent = details.open ? '−' : '+';
        }
      });
    });
  }

})();
